import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Box,
  Paper,
  Container,
  Avatar,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  LinearProgress,
  Badge,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import WorkIcon from '@mui/icons-material/Work';
import ChatIcon from '@mui/icons-material/Chat';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TaskIcon from '@mui/icons-material/Task';
import StorageIcon from '@mui/icons-material/Storage';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { 
  getHiredAgents, 
  updateHiredAgent, 
  offboardAgent, 
  getAgentPerformance,
  onboardAgent
} from '../../api';
import { useGlobalContext } from '../../GlobalProvider';

const AgentOverview = ({ onChatClick }) => {
  const { appId } = useGlobalContext();
  const navigate = useNavigate();
  const [hiredAgents, setHiredAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [onboardDialogOpen, setOnboardDialogOpen] = useState(false);
  const [offboardDialogOpen, setOffboardDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuAgent, setMenuAgent] = useState(null);
  const [configData, setConfigData] = useState({
    customContext: '',
    externalContext: '',
    status: 'Active',
    emailConfig: {
      isEmailEnabled: true,
      emailSignature: ''
    },
    settings: {
      timezone: 'UTC',
      workingHours: {
        start: '09:00',
        end: '17:00'
      }
    }
  });
  const [onboardData, setOnboardData] = useState({
    customContext: '',
    externalContext: '',
    emailConfig: {
      isEmailEnabled: true,
      emailSignature: ''
    },
    settings: {
      timezone: 'UTC',
      workingHours: {
        start: '09:00',
        end: '17:00'
      }
    }
  });
  const [offboardData, setOffboardData] = useState({
    billingEndDate: '',
    reason: ''
  });

  // Fetch hired agents
  const fetchHiredAgents = async () => {
    if (!appId) {
      setError('App ID is not available. Please switch to an app first.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await getHiredAgents(appId);
      setHiredAgents(response.data.data);
    } catch (err) {
      console.error('Error fetching hired agents:', err);
      setError('Failed to load hired agents. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when appId changes
  useEffect(() => {
    if (appId) {
      fetchHiredAgents();
    }
  }, [appId]);

  // Handle chat with agent
  const handleChatClick = (agent) => {
    // Navigate to the agent workspace with chat tab
    navigate(`/agent-workspace/${agent._id}?tab=chat`);
  };

  // Handle menu open
  const handleMenuOpen = (event, agent) => {
    setMenuAnchor(event.currentTarget);
    setMenuAgent(agent);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuAgent(null);
  };

  // Handle menu item click
  const handleMenuItemClick = (action) => {
    handleMenuClose();
    
    switch (action) {
      case 'configure':
        handleConfigClick(menuAgent);
        break;
      case 'onboard':
        handleOnboardClick(menuAgent);
        break;
      case 'offboard':
        handleOffboardClick(menuAgent);
        break;
      case 'tasks':
        navigate(`/agent-workspace/${menuAgent._id}?tab=tasks`);
        break;
      case 'performance':
        navigate(`/agent-workspace/${menuAgent._id}?tab=performance`);
        break;
      case 'context':
        navigate(`/agent-workspace/${menuAgent._id}?tab=context`);
        break;
      case 'view':
        navigate(`/agent-workspace/${menuAgent._id}`);
        break;
      default:
        break;
    }
  };

  // Handle agent configuration
  const handleConfigClick = (agent) => {
    setSelectedAgent(agent);
    setConfigData({
      customContext: agent.customContext || '',
      externalContext: agent.externalContext || '',
      status: agent.status,
      emailConfig: {
        isEmailEnabled: agent.emailConfig?.isEmailEnabled !== false,
        emailSignature: agent.emailConfig?.emailSignature || ''
      },
      settings: {
        timezone: agent.settings?.timezone || 'UTC',
        workingHours: {
          start: agent.settings?.workingHours?.start || '09:00',
          end: agent.settings?.workingHours?.end || '17:00'
        }
      }
    });
    setConfigDialogOpen(true);
  };

  // Handle agent onboarding
  const handleOnboardClick = (agent) => {
    setSelectedAgent(agent);
    setOnboardData({
      customContext: '',
      externalContext: '',
      emailConfig: {
        isEmailEnabled: true,
        emailSignature: ''
      },
      settings: {
        timezone: 'UTC',
        workingHours: {
          start: '09:00',
          end: '17:00'
        }
      }
    });
    setOnboardDialogOpen(true);
  };

  // Handle onboarding submission
  const handleOnboardSubmit = async () => {
    try {
      await onboardAgent(selectedAgent._id, onboardData, appId);
      setOnboardDialogOpen(false);
      fetchHiredAgents(); // Refresh data
    } catch (err) {
      console.error('Error onboarding agent:', err);
      setError('Failed to onboard agent. Please try again.');
    }
  };

  // Save agent configuration
  const handleSaveConfig = async () => {
    try {
      await updateHiredAgent(selectedAgent._id, configData, appId);
      setConfigDialogOpen(false);
      fetchHiredAgents(); // Refresh data
    } catch (err) {
      console.error('Error updating agent:', err);
      setError('Failed to update agent configuration.');
    }
  };

  // Offboard agent
  const handleOffboardClick = (agent) => {
    setSelectedAgent(agent);
    setOffboardData({
      billingEndDate: '',
      reason: ''
    });
    setOffboardDialogOpen(true);
  };

  // Handle offboarding submission
  const handleOffboardSubmit = async () => {
    try {
      await offboardAgent(selectedAgent._id, offboardData, appId);
      setOffboardDialogOpen(false);
      fetchHiredAgents(); // Refresh data
    } catch (err) {
      console.error('Error offboarding agent:', err);
      setError('Failed to offboard agent. Please try again.');
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Busy': return 'warning';
      case 'Available': return 'info';
      case 'Inactive': return 'error';
      case 'Training': return 'secondary';
      case 'Offboarded': return 'error';
      default: return 'default';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active': return <CheckCircleIcon />;
      case 'Busy': return <AccessTimeIcon />;
      case 'Available': return <WorkIcon />;
      case 'Inactive': return <DeleteIcon />;
      case 'Training': return <WorkIcon />;
      case 'Offboarded': return <DeleteIcon />;
      default: return <WorkIcon />;
    }
  };

  // Format last active time
  const formatLastActive = (lastActive) => {
    if (!lastActive) return 'Never';
    const now = new Date();
    const last = new Date(lastActive);
    const diffMs = now - last;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading your AI workforce...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Paper 
        elevation={0}
        sx={{ 
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white',
          p: 4,
          mb: 4,
          borderRadius: 4
        }}
      >
        <Typography variant="h3" gutterBottom fontWeight="bold">
          My AI Employees
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
          Manage your AI workforce and monitor their performance
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold">{hiredAgents.length}</Typography>
              <Typography variant="body1">Total Employees</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold">
                {hiredAgents.filter(e => e.status === 'Active').length}
              </Typography>
              <Typography variant="body1">Currently Active</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold">
                {hiredAgents.reduce((sum, e) => sum + (e.performance?.tasksCompleted || 0), 0)}
              </Typography>
              <Typography variant="body1">Tasks Completed</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Employee Cards Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {hiredAgents.map((agent) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={agent._id}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                border: '1px solid rgba(0,0,0,0.05)',
                '&:hover': { 
                  transform: 'translateY(-8px)', 
                  boxShadow: '0 16px 48px rgba(0,0,0,0.15)',
                  border: '1px solid rgba(79, 172, 254, 0.3)'
                },
                position: 'relative',
                minHeight: 320,
                overflow: 'visible'
              }}
            >
              {/* Header with Avatar and Status */}
              <Box sx={{ 
                p: 3, 
                pb: 2, 
                textAlign: 'center',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                position: 'relative'
              }}>
                <Avatar
                  src={agent.icon}
                  alt={agent.name}
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    mx: 'auto', 
                    mb: 2,
                    border: '4px solid white',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                  }}
                />
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                  {agent.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {agent.category} Specialist
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    icon={getStatusIcon(agent.status)}
                    label={agent.status}
                    color={getStatusColor(agent.status)}
                    size="small"
                    sx={{ fontWeight: 'bold' }}
                  />
                  {!agent.isConfigured && (
                    <Chip
                      label="Needs Onboarding"
                      color="warning"
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                </Box>
              </Box>

              <CardContent sx={{ p: 3, pt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ 
                  minHeight: 40, 
                  mb: 2,
                  lineHeight: 1.5
                }}>
                  {agent.shortDescription}
                </Typography>
                

                
                {/* Performance Metrics */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Performance
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {agent.performance?.successRate || 0}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={agent.performance?.successRate || 0}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>

                {/* Stats */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <WorkIcon sx={{ fontSize: 16 }} />
                    {agent.performance?.tasksCompleted || 0} tasks completed
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 16 }} />
                    {agent.performance?.hoursWorked || 0} hours worked
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Last active: {formatLastActive(agent.performance?.lastActive)}
                  </Typography>
                </Box>

                {/* Email Address */}
                {agent.emailConfig?.emailAddress && (
                  <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <EmailIcon sx={{ fontSize: 14 }} />
                      {agent.emailConfig.emailAddress}
                    </Typography>
                  </Box>
                )}

                {/* Offboarding Information */}
                {agent.status === 'Offboarded' && agent.billingEndDate && (
                  <Box sx={{ mb: 2, p: 1, bgcolor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.200' }}>
                    <Typography variant="caption" color="error.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                      Offboarded on {new Date(agent.offboardedAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="error.main">
                      Billing ends: {new Date(agent.billingEndDate).toLocaleDateString()}
                    </Typography>
                    {agent.offboardingReason && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Reason: {agent.offboardingReason}
                      </Typography>
                    )}
                  </Box>
                )}
              </CardContent>

                {/* Action Buttons */}
                <Box sx={{ 
                  p: 2, 
                  pt: 0, 
                  display: 'flex', 
                  gap: 1,
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  {agent.status === 'Offboarded' ? (
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block', width: '100%' }}>
                      Agent has been offboarded
                    </Typography>
                  ) : agent.isConfigured ? (
                    <>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ChatIcon />}
                        onClick={() => handleChatClick(agent)}
                        sx={{ 
                          flex: 1,
                          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #3a9bed 0%, #00d4e0 100%)'
                          }
                        }}
                      >
                        Chat
                      </Button>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, agent)}
                        sx={{ 
                          bgcolor: 'grey.100',
                          '&:hover': { bgcolor: 'grey.200' }
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<SettingsIcon />}
                        onClick={() => handleOnboardClick(agent)}
                        sx={{ 
                          flex: 1,
                          bgcolor: 'warning.main',
                          '&:hover': { bgcolor: 'warning.dark' }
                        }}
                      >
                        Onboard
                      </Button>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, agent)}
                        sx={{ 
                          bgcolor: 'grey.100',
                          '&:hover': { bgcolor: 'grey.200' }
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Empty State */}
      {hiredAgents.length === 0 && !loading && (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h5" gutterBottom color="text.secondary">
            No AI Employees Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Hire your first AI specialist from the marketplace to get started
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            onClick={() => window.location.href = '/agent-marketplace'}
            sx={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #3a9bed 0%, #00d4e0 100%)'
              }
            }}
          >
            Browse Marketplace
          </Button>
        </Paper>
      )}

      {/* Configuration Dialog */}
      <Dialog 
        open={configDialogOpen} 
        onClose={() => setConfigDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Configure {selectedAgent?.name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Custom Context"
                multiline
                rows={3}
                value={configData.customContext}
                onChange={(e) => setConfigData({...configData, customContext: e.target.value})}
                helperText="Custom instructions and context for this agent"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="External Context"
                multiline
                rows={2}
                value={configData.externalContext}
                onChange={(e) => setConfigData({...configData, externalContext: e.target.value})}
                helperText="External data sources, APIs, or connections"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={configData.status}
                  label="Status"
                  onChange={(e) => setConfigData({...configData, status: e.target.value})}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Busy">Busy</MenuItem>
                  <MenuItem value="Available">Available</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="Training">Training</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={configData.settings.timezone}
                  label="Timezone"
                  onChange={(e) => setConfigData({
                    ...configData, 
                    settings: {...configData.settings, timezone: e.target.value}
                  })}
                >
                  <MenuItem value="UTC">UTC</MenuItem>
                  <MenuItem value="America/New_York">Eastern Time</MenuItem>
                  <MenuItem value="America/Chicago">Central Time</MenuItem>
                  <MenuItem value="America/Denver">Mountain Time</MenuItem>
                  <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Working Hours Start"
                type="time"
                value={configData.settings.workingHours.start}
                onChange={(e) => setConfigData({
                  ...configData, 
                  settings: {
                    ...configData.settings, 
                    workingHours: {...configData.settings.workingHours, start: e.target.value}
                  }
                })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Working Hours End"
                type="time"
                value={configData.settings.workingHours.end}
                onChange={(e) => setConfigData({
                  ...configData, 
                  settings: {
                    ...configData.settings, 
                    workingHours: {...configData.settings.workingHours, end: e.target.value}
                  }
                })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={configData.emailConfig.isEmailEnabled}
                    onChange={(e) => setConfigData({
                      ...configData, 
                      emailConfig: {...configData.emailConfig, isEmailEnabled: e.target.checked}
                    })}
                  />
                }
                label="Enable Email Communication"
              />
            </Grid>
            {configData.emailConfig.isEmailEnabled && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Signature"
                  multiline
                  rows={2}
                  value={configData.emailConfig.emailSignature}
                  onChange={(e) => setConfigData({
                    ...configData, 
                    emailConfig: {...configData.emailConfig, emailSignature: e.target.value}
                  })}
                  helperText="Custom signature for agent emails"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveConfig} variant="contained">Save Configuration</Button>
        </DialogActions>
      </Dialog>

      {/* Onboarding Dialog */}
      <Dialog 
        open={onboardDialogOpen} 
        onClose={() => setOnboardDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Onboard {selectedAgent?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Configure your new AI employee to get them ready for work.
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Custom Context *"
                multiline
                rows={4}
                value={onboardData.customContext}
                onChange={(e) => setOnboardData({...onboardData, customContext: e.target.value})}
                helperText="Required: Custom instructions and context for this agent"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="External Context"
                multiline
                rows={3}
                value={onboardData.externalContext}
                onChange={(e) => setOnboardData({...onboardData, externalContext: e.target.value})}
                helperText="External data sources, APIs, or connections"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  value={onboardData.settings.timezone}
                  label="Timezone"
                  onChange={(e) => setOnboardData({
                    ...onboardData, 
                    settings: {...onboardData.settings, timezone: e.target.value}
                  })}
                >
                  <MenuItem value="UTC">UTC</MenuItem>
                  <MenuItem value="America/New_York">Eastern Time</MenuItem>
                  <MenuItem value="America/Chicago">Central Time</MenuItem>
                  <MenuItem value="America/Denver">Mountain Time</MenuItem>
                  <MenuItem value="America/Los_Angeles">Pacific Time</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Working Hours Start"
                type="time"
                value={onboardData.settings.workingHours.start}
                onChange={(e) => setOnboardData({
                  ...onboardData, 
                  settings: {
                    ...onboardData.settings, 
                    workingHours: {...onboardData.settings.workingHours, start: e.target.value}
                  }
                })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Working Hours End"
                type="time"
                value={onboardData.settings.workingHours.end}
                onChange={(e) => setOnboardData({
                  ...onboardData, 
                  settings: {
                    ...onboardData.settings, 
                    workingHours: {...onboardData.settings.workingHours, end: e.target.value}
                  }
                })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={onboardData.emailConfig.isEmailEnabled}
                    onChange={(e) => setOnboardData({
                      ...onboardData, 
                      emailConfig: {...onboardData.emailConfig, isEmailEnabled: e.target.checked}
                    })}
                  />
                }
                label="Enable Email Communication"
              />
            </Grid>
            {onboardData.emailConfig.isEmailEnabled && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Signature"
                  multiline
                  rows={2}
                  value={onboardData.emailConfig.emailSignature}
                  onChange={(e) => setOnboardData({
                    ...onboardData, 
                    emailConfig: {...onboardData.emailConfig, emailSignature: e.target.value}
                  })}
                  helperText="Custom signature for agent emails"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOnboardDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleOnboardSubmit} 
            variant="contained"
            disabled={!onboardData.customContext.trim()}
          >
            Complete Onboarding
          </Button>
        </DialogActions>
      </Dialog>

      {/* Offboarding Dialog */}
      <Dialog 
        open={offboardDialogOpen} 
        onClose={() => setOffboardDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Offboard {selectedAgent?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Please provide the billing end date and reason for offboarding this agent.
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Billing End Date *"
                type="date"
                value={offboardData.billingEndDate}
                onChange={(e) => setOffboardData({...offboardData, billingEndDate: e.target.value})}
                InputLabelProps={{ shrink: true }}
                required
                helperText="When should billing stop for this agent?"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Offboarding"
                multiline
                rows={3}
                value={offboardData.reason}
                onChange={(e) => setOffboardData({...offboardData, reason: e.target.value})}
                helperText="Optional: Provide a reason for offboarding this agent"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOffboardDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleOffboardSubmit} 
            variant="contained"
            color="error"
            disabled={!offboardData.billingEndDate}
          >
            Offboard Agent
          </Button>
        </DialogActions>
      </Dialog>

      {/* Agent Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 200,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            borderRadius: 2
          }
        }}
      >
        {menuAgent && !menuAgent.isConfigured && (
          <MenuItem onClick={() => handleMenuItemClick('onboard')}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Onboard Agent</ListItemText>
          </MenuItem>
        )}
        
        {menuAgent && menuAgent.isConfigured && (
          <>
            <MenuItem onClick={() => handleMenuItemClick('configure')}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Configure Agent</ListItemText>
            </MenuItem>
            
            <MenuItem onClick={() => handleMenuItemClick('tasks')}>
              <ListItemIcon>
                <TaskIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Manage Tasks</ListItemText>
            </MenuItem>
            
            <MenuItem onClick={() => handleMenuItemClick('context')}>
              <ListItemIcon>
                <StorageIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Data & Context</ListItemText>
            </MenuItem>
            
            <MenuItem onClick={() => handleMenuItemClick('performance')}>
              <ListItemIcon>
                <AnalyticsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Performance Analytics</ListItemText>
            </MenuItem>
            
            <MenuItem onClick={() => handleMenuItemClick('view')}>
              <ListItemIcon>
                <VisibilityIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>View Details</ListItemText>
            </MenuItem>
          </>
        )}
        
        {menuAgent && menuAgent.status !== 'Offboarded' && (
          <MenuItem 
            onClick={() => handleMenuItemClick('offboard')}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Offboard Agent</ListItemText>
          </MenuItem>
        )}
      </Menu>


    </Container>
  );
};

export default AgentOverview; 